import { useState } from 'react';
import { useNavigate } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { BatchPhase } from '@autopilot/contracts';
import { api, type BatchListQuery } from '../api/client';
import { qk, useNameLookup, useRoles } from '../api/hooks';
import { ModeBadge, PhaseBadge } from '../components/Badges';
import { PageHeader } from '../components/PageHeader';
import { EmptyState, QueryBoundary } from '../components/States';
import { TokenValue } from '../components/TokenValue';
import { CLOSE_REASON_LABEL, PHASE_LABEL } from '../lib/labels';
import { formatDateTime, shortId } from '../lib/format';
import { summarizeFilter } from '../lib/filters';
import { authorizationText } from './BatchPage';

export function BatchHistoryPage() {
  const nav = useNavigate();
  const { isOperator } = useRoles();
  const names = useNameLookup();
  const [trigger, setTrigger] = useState<'' | 'MANUAL' | 'SCHEDULED'>('');
  const [phase, setPhase] = useState('');
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors.at(-1);
  const params: BatchListQuery = { cursor, limit: 25, trigger: trigger || undefined, phase: phase || undefined };
  const q = useQuery({ queryKey: qk.batches(params), queryFn: () => api.listBatches(params), placeholderData: keepPreviousData });
  const items = q.data?.items ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Historie"
        title="Batch-Historie"
        description="Manuelle und automatische Batches Ihrer berechtigten Gesellschaften mit Freigabenachweis, Ergebnissen und Verbrauch."
        actions={
          isOperator ? (
            <Button variant="contained" startIcon={<Plus size={18} />} onClick={() => nav('/batches/new')}>
              Neuer Batch
            </Button>
          ) : undefined
        }
      />
      <Card>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField select label="Modus" value={trigger} onChange={(e) => { setTrigger(e.target.value as typeof trigger); setCursors([undefined]); }} sx={{ minWidth: 180 }}>
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="MANUAL">Manuell</MenuItem>
            <MenuItem value="SCHEDULED">Automatisch</MenuItem>
          </TextField>
          <TextField select label="Status" value={phase} onChange={(e) => { setPhase(e.target.value); setCursors([undefined]); }} sx={{ minWidth: 220 }}>
            <MenuItem value="">Alle</MenuItem>
            {BatchPhase.options.map((p) => (
              <MenuItem key={p} value={p}>
                {PHASE_LABEL[p].label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <QueryBoundary query={q} action="Batch-Historie laden" loadingRows={8}>
          {items.length === 0 ? (
            <EmptyState title="Noch keine Batches" description={isOperator ? 'Legen Sie über „Neuer Batch“ den ersten Batch an.' : 'Sobald Batches für Ihre Gesellschaften existieren, erscheinen sie hier.'} />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="Batch-Historie" sx={{ minWidth: 1000, '& td, & th': { px: 1.25 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Batch</TableCell>
                    <TableCell>Modus</TableCell>
                    <TableCell>Freigabe / Auslöser</TableCell>
                    <TableCell>Zeit</TableCell>
                    <TableCell>Filter</TableCell>
                    <TableCell>Modell</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Lösungen</TableCell>
                    <TableCell align="right">Tokens</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((b) => (
                    <TableRow key={b.id} hover sx={{ cursor: 'pointer' }} onClick={() => nav(`/batches/${b.id}`)} data-testid="history-row">
                      <TableCell>
                        <Link component="button" onClick={(e) => { e.stopPropagation(); nav(`/batches/${b.id}`); }} sx={{ textAlign: 'left' }}>
                          {b.name ?? `Batch ${shortId(b.id)}`}
                        </Link>
                        <Typography variant="caption" component="div" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                          {shortId(b.id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ModeBadge trigger={b.trigger} />
                      </TableCell>
                      <TableCell>{authorizationText(b)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(b.scheduledFor ?? b.createdAt)}</TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {b.filter
                            ? summarizeFilter(b.filter, names)
                            : b.gesellschaftIds.length
                              ? b.gesellschaftIds.map((id) => names.gesellschaften.get(id) ?? 'Gesellschaft').join(', ')
                              : 'Alle Gesellschaften'}
                        </Typography>
                      </TableCell>
                      <TableCell>{b.generator?.name ?? '–'}</TableCell>
                      <TableCell>
                        <PhaseBadge phase={b.phase} />
                        {b.closeReason && b.closeReason !== 'COMPLETED' && (
                          <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                            {CLOSE_REASON_LABEL[b.closeReason]}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {b.resolvedCount}
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: 120 }}>
                        <TokenValue usage={b.usage} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </QueryBoundary>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" startIcon={<ChevronLeft size={16} />} disabled={cursors.length <= 1} onClick={() => setCursors((c) => c.slice(0, -1))}>
            Zurück
          </Button>
          <Button size="small" endIcon={<ChevronRight size={16} />} disabled={!q.data?.nextCursor} onClick={() => setCursors((c) => [...c, q.data!.nextCursor!])}>
            Weiter
          </Button>
        </Box>
      </Card>
    </>
  );
}
